import { useCallback, useMemo, useRef } from 'react';

export type UseFadingEdgeOptions = {
    /** Scroll axis of the container (default 'vertical'). */
    orientation?: 'horizontal' | 'vertical';
    /** Maximum size of a fade edge in px (default 48). */
    maxFadeSize?: number;
    /**
     * Scroll distance in px over which a fade ramps from 0 to its maximum
     * (default: `maxFadeSize`, like the native Android fading edge).
     */
    fadeDistance?: number;
};

/**
 * Scroll-position-aware fading edges for a scroll container, like Android's `fadingEdge` /
 * the mobile app's FadingEdgeView: an edge only fades when there is more content in that
 * direction, and the fade grows gradually with the scroll offset instead of popping in.
 *
 * Attach the returned callback ref to the scroll container. The mask is written straight to
 * the element's inline style (rAF-throttled), so scrolling causes no React re-renders.
 * Content and size changes are picked up via Mutation-/ResizeObserver.
 *
 * ```tsx
 * const { ref: fadingEdgeRef } = useFadingEdge();
 * <div ref={fadingEdgeRef} className="overflow-y-auto"> ... </div>
 * ```
 */
export function useFadingEdge(options?: UseFadingEdgeOptions): { ref: (node: HTMLElement | null) => void } {
    const { orientation = 'vertical', maxFadeSize = 32, fadeDistance = options?.maxFadeSize ?? 32 } = options ?? {};

    const cleanupRef = useRef<(() => void) | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    const ref = useCallback(
        (node: HTMLElement | null) => {
            cleanupRef.current?.();
            cleanupRef.current = null;

            if (!node) {
                return;
            }

            const isVertical = orientation === 'vertical';
            let lastMaskImage: string | null = null;

            function updateFade(): void {
                animationFrameRef.current = null;

                const scrollSize = isVertical ? node.scrollHeight : node.scrollWidth;
                const clientSize = isVertical ? node.clientHeight : node.clientWidth;
                const scrollOffset = isVertical ? node.scrollTop : node.scrollLeft;
                const maxScrollOffset = scrollSize - clientSize;

                // Sub-pixel rounding keeps the mask string stable, so mid-list scrolling
                // (both fades saturated) skips the style write entirely.
                const startFade = Math.round(Math.min(scrollOffset / fadeDistance, 1) * maxFadeSize);
                const endFade = Math.round(Math.min((maxScrollOffset - scrollOffset) / fadeDistance, 1) * maxFadeSize);

                const maskImage =
                    maxScrollOffset <= 1
                        ? ''
                        : `linear-gradient(${isVertical ? 'to bottom' : 'to right'}, ` +
                          `transparent 0px, rgb(0 0 0) ${startFade}px, ` +
                          `rgb(0 0 0) calc(100% - ${endFade}px), transparent 100%)`;

                if (maskImage === lastMaskImage) {
                    return;
                }
                lastMaskImage = maskImage;
                node.style.maskImage = maskImage;
                node.style.webkitMaskImage = maskImage;
            }

            function scheduleUpdate(): void {
                if (animationFrameRef.current !== null) {
                    return;
                }
                animationFrameRef.current = requestAnimationFrame(updateFade);
            }

            updateFade();
            node.addEventListener('scroll', scheduleUpdate, { passive: true });

            // Async content (infinite scroll, date/filter changes) alters scrollHeight without a
            // scroll event — observe child mutations and container resizes as well.
            const mutationObserver = new MutationObserver(scheduleUpdate);
            mutationObserver.observe(node, { childList: true, subtree: true });
            const resizeObserver = new ResizeObserver(scheduleUpdate);
            resizeObserver.observe(node);

            cleanupRef.current = () => {
                node.removeEventListener('scroll', scheduleUpdate);
                mutationObserver.disconnect();
                resizeObserver.disconnect();
                if (animationFrameRef.current !== null) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
                node.style.maskImage = '';
                node.style.webkitMaskImage = '';
            };
        },
        [orientation, maxFadeSize, fadeDistance]
    );

    return useMemo(() => ({ ref }), [ref]);
}

export default useFadingEdge;
